import { useEffect } from 'react';
import { NavLink, useSearchParams } from '@tov-plus/frontend-data';
import { useTovQuery } from '@tov-plus/frontend-data';
import { replaceSearchParam } from '@tov-plus/utils';
import {
  TovButton,
  Input,
  PageGrid,
  TovField,
  msg,
  TovCard,
  useTovForm,
  useTovFormStore,
} from '@tov-plus/ui';

import { guestApi } from '../app/api';
import { StatePanel } from '../components/StatePanel';
import { useGuestAccess } from '../contexts/GuestAccessContext';
import { guestQueryKeys } from '../lib/data/guestQueryKeys';
import { getGuestErrorMessage } from '../lib/errors/guestErrors';

const guestSearchEyebrow = () => msg('general.fields.search.label');
const guestSearchTitle = () => msg('general.fields.search.label');
const guestSearchDescription = () =>
  msg('app.navigation.guest_visible_event_summaries_for_the_current_occasion');
const guestSearchPlaceholder = () => msg('general.fields.search.label');
const guestSearchResultsLabel = () => msg('general.fields.search.label');
const searchErrorMessage = () =>
  msg('pages.search_page.guest_search_results_load_error');

function resolveSearchTargetPath(
  document: Awaited<
    ReturnType<typeof guestApi.searchOccasionResources>
  >['items'][number]['document'],
) {
  const rawTarget =
    typeof document.metadata.navigation_target === 'string'
      ? document.metadata.navigation_target
      : null;

  if (rawTarget) {
    return rawTarget.startsWith('/') ? rawTarget : `/${rawTarget}`;
  }

  switch (document.resource_type) {
    case 'event':
      return '/events';
    case 'guest_faq':
    case 'guest_content':
    case 'published_page':
      return '/';
    default:
      return '/today';
  }
}

export function SearchPage() {
  const access = useGuestAccess();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') ?? '';
  const normalizedQuery = query.trim();
  const searchForm = useTovForm({
    defaultValues: { query },
    onSubmit: ({ value }) => {
      setSearchParams(
        replaceSearchParam(searchParams, 'q', value.query.trim()),
        {
          replace: true,
        },
      );
    },
  });
  const searchValues = useTovFormStore(
    searchForm.store,
    (state) => state.values,
  );
  const readyOccasionSlug =
    access.status === 'ready' ? access.experience.occasion.slug : null;
  const readyGuestToken = access.status === 'ready' ? access.guestToken : null;
  const searchQuery = useTovQuery({
    queryKey:
      readyOccasionSlug && readyGuestToken && normalizedQuery
        ? guestQueryKeys.search(
            readyOccasionSlug,
            readyGuestToken,
            normalizedQuery,
          )
        : guestQueryKeys.search('pending', 'pending', ''),
    queryFn: () =>
      guestApi.searchOccasionResources(readyOccasionSlug!, readyGuestToken!, {
        query: normalizedQuery,
        resourceTypes: [
          'event',
          'guest_faq',
          'published_page',
          'guest_content',
        ],
        includeAnalytics: true,
      }),
    enabled: Boolean(readyOccasionSlug && readyGuestToken && normalizedQuery),
  });

  useEffect(() => {
    searchForm.setFieldValue('query', query);
  }, [query, searchForm]);

  if (access.status !== 'ready') {
    return null;
  }

  return (
    <PageGrid>
      <TovCard variant="content" isWide data-testid="guest-search-page">
        <p className="page-eyebrow">{guestSearchEyebrow()}</p>
        <h3>{guestSearchTitle()}</h3>
        <p>{guestSearchDescription()}</p>
        <form
          className="search-form"
          onSubmit={(event) => {
            event.preventDefault();
            void searchForm.handleSubmit();
          }}
        >
          <TovField
            className="search-form__field"
            label={msg('general.fields.search.label')}
          >
            <Input
              name="q"
              value={searchValues.query}
              onChange={(event) =>
                searchForm.setFieldValue('query', event.target.value)
              }
              placeholder={guestSearchPlaceholder()}
            />
          </TovField>
          <TovButton type="submit">
            {msg('general.fields.search.label')}
          </TovButton>
        </form>
      </TovCard>

      {searchQuery.isLoading ? <StatePanel preset="loading" /> : null}
      {searchQuery.isError ? (
        <StatePanel
          preset="unknown-error"
          detail={getGuestErrorMessage(searchQuery.error, searchErrorMessage())}
        />
      ) : null}
      {searchQuery.isSuccess && searchQuery.data ? (
        <TovCard variant="content" isWide>
          <p className="page-eyebrow">{guestSearchResultsLabel()}</p>
          <h3>
            {searchQuery.data.total} {guestSearchResultsLabel().toLowerCase()}
          </h3>
          <div className="detail-list">
            {searchQuery.data.items.map((item) => (
              <article key={item.document.document_id}>
                <strong>{item.document.title}</strong>
                <span>
                  {item.document.subtitle ?? item.document.resource_type}
                </span>
                <span>
                  <NavLink
                    className="secondary-link"
                    to={resolveSearchTargetPath(item.document)}
                  >
                    {item.document.title}
                  </NavLink>
                </span>
              </article>
            ))}
          </div>
        </TovCard>
      ) : null}
    </PageGrid>
  );
}
